import React from 'react';
import { StyleSheet, Image } from 'react-native';
import { Avatar, Menu } from 'react-native-paper';
import { useRouter } from 'expo-router';
import styled from '@emotion/native';
import { green, white, sand } from '@/shared/styles/colors';
import {
  DropdownMenuIconType,
  DropdownMenuItem,
} from '@/shared/types/navigation';
import { DROPDOWN_MENU_ITEMS } from '@/shared/constants/navigation';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { useApollo } from '@/services/apollo/ApolloProvider';
import { formatNameWithLastInitial } from '@/shared/utils';

interface DropdownMenuProps {
  isVisible: boolean;
  onDismiss: () => void;
  onPress: () => void;
}

const DropdownMenu = ({ isVisible, onDismiss, onPress }: DropdownMenuProps) => {
  const router = useRouter();
  const { logOut } = useApollo();
  const { user } = useGetUser();

  const renderIcon = ({
    source,
    width = 16,
    height = 16,
  }: DropdownMenuIconType) => {
    const IconComponent = () => (
      <Image
        source={source}
        style={[styles.icon, { width, height }]}
        resizeMode="contain"
      />
    );
    return IconComponent;
  };

  const handleItemPress = async (item: DropdownMenuItem) => {
    if (item.route === 'logout') {
      logOut();
    } else if ('action' in item.route) {
      item.route.action();
    } else {
      router.push(item.route.path);
    }
    onDismiss();
  };

  const renderAvatar = () => {
    const stylistName = formatNameWithLastInitial(user).toUpperCase();
    const profileImage = user?.profilePicUrl ?? '';

    if (profileImage) {
      return (
        <Avatar.Image
          size={45}
          source={{ uri: profileImage }}
          style={{ backgroundColor: green }}
        />
      );
    }

    return (
      <Avatar.Text
        size={45}
        color={white}
        style={{ backgroundColor: green }}
        label={
          stylistName
            ? stylistName
                .split(' ')
                .map((n) => n[0])
                .join('')
            : '?'
        }
      />
    );
  };

  return (
    <MenuWrapper>
      <Menu
        visible={isVisible}
        onDismiss={onDismiss}
        anchorPosition="bottom"
        contentStyle={styles.menuContent}
        style={styles.menu}
        anchor={<AvatarButton onPress={onPress}>{renderAvatar()}</AvatarButton>}
      >
        <Triangle />
        {DROPDOWN_MENU_ITEMS.map((item) => (
          <Menu.Item
            key={item.label}
            onPress={() => handleItemPress(item)}
            title={item.label}
            leadingIcon={renderIcon(item.leadingIcon)}
            trailingIcon={
              item.trailingIcon ? renderIcon(item.trailingIcon) : undefined
            }
            style={styles.menuItem}
            titleStyle={styles.menuItemTitle}
          />
        ))}
      </Menu>
    </MenuWrapper>
  );
};

export default DropdownMenu;

const MenuWrapper = styled.View({
  position: 'relative',
});

const AvatarButton = styled.TouchableOpacity({
  padding: 4,
});

const styles = StyleSheet.create({
  menu: {
    width: 200,
    minHeight: 196,
    marginTop: 24,
    transform: [{ translateX: -5 }],
  },
  menuContent: {
    backgroundColor: sand,
    borderRadius: 10,
    width: 200,
    paddingVertical: 14,
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
  },
  menuItem: {
    paddingLeft: 20,
    paddingRight: 32,
    height: 36,
    marginLeft: 8,
    marginRight: 8,
    backgroundColor: sand,
  },
  menuItemTitle: {
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: -0.14,
    color: green,
    flex: 1,
    marginLeft: 0,
  },
  icon: {
    width: 16,
    height: 16,
  },
});

const Triangle = styled.View<{ right?: number; size?: number }>(
  ({ right = 4, size = 16 }) => ({
    position: 'absolute',
    top: -size,
    right: right,
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: size,
    borderRightWidth: size,
    borderBottomWidth: size,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: sand,
    zIndex: 1,
  }),
);
